import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from 'axios';
import { motion } from "framer-motion";
import { Calendar, User, Clock, ArrowLeft, CheckCircle, Share2, Bookmark, ChevronRight } from "lucide-react";
import { getImageUrl } from "../../utils/helpers";

const BlogInner = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [banner, setBanner] = useState({
    title: 'Housing Loan Insights',
    subtitle: 'Expert advice and guides for your home financing journey',
    image_url: 'img/Blogs/blog-banner-new.png'
  });
  const [ctaSettings, setCtaSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch banner
        const bannerRes = await axios.get('/api/admin/banners/active');
        const blogBanner = (bannerRes.data.data || []).find(b => b.page_type === 'blog');
        if (blogBanner) {
          setBanner(blogBanner);
        }

        // Fetch all active posts
        const postsRes = await axios.get('/api/blog/active');
        if (postsRes.data.success) {
          const allPosts = postsRes.data.data;
          const foundPost = allPosts.find(p => p.id === parseInt(id));
          setPost(foundPost);
          setTrendingPosts(allPosts.filter(p => p.id !== parseInt(id)).slice(0, 4));
        }

        // Fetch CTA Settings
        const ctaRes = await axios.get('/api/admin/blog/cta-settings');
        if (ctaRes.data.success) {
          setCtaSettings(ctaRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching blog post data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Blog Post Not Found</h2>
          <Link to="/home-loan-blog-aham-housing-finance" className="text-primary-600 hover:text-primary-700 font-semibold flex items-center justify-center gap-2">
            <ArrowLeft className="h-5 w-5" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Fallback for missing data and parse JSON strings (Backward compatibility)
  const longDescription = (() => {
    if (!post.long_description) return `<p>${post.excerpt}</p>`;
    if (Array.isArray(post.long_description)) return post.long_description.map(p => `<p>${p}</p>`).join('');
    if (typeof post.long_description === 'string') {
      try {
        const parsed = JSON.parse(post.long_description);
        if (Array.isArray(parsed)) return parsed.map(p => `<p>${p}</p>`).join('');
        return post.long_description;
      } catch (e) {
        return post.long_description;
      }
    }
    return `<p>${post.excerpt}</p>`;
  })();

  const highlights = Array.isArray(post.highlights)
    ? post.highlights
    : (typeof post.highlights === 'string' ? JSON.parse(post.highlights) : []);

  const subHeading = post.title;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">

      {/* Hero Banner Section - Static (Same as Blog Listing Page) */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        {/* Background Image - Static */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url("${getImageUrl(banner.image_url)}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content - Static */}
        <div className="relative z-10 container mx-auto px-4 py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner.title}
            </h1>
            <p className="text-2xl mb-8 md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed">
              {banner.subtitle}
            </p>

            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2 mb-6"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRight className="h-4 w-4 text-white/60" />
              <Link
                to="/home-loan-blog-aham-housing-finance"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Blog
              </Link>
              <ChevronRight className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Article</span>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* 🔵 MAIN CONTENT BODY */}
      <div className="container mx-auto px-4 py-16 -mt-20 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* Left Content Column */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white rounded-3xl shadow-xl p-8 md:p-12 overflow-hidden"
            >
              {/* Post Meta Only */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <div className="flex flex-wrap items-center gap-6 text-gray-600">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary-600" />
                    <span className="text-sm font-medium">{post.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary-600" />
                    <span className="text-sm font-medium">{post.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary-600" />
                    <span className="text-sm font-medium">{post.read_time}</span>
                  </div>
                </div>
              </div>

              {/* Main Heading (Second Heading) */}
              <div className="mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-snug">
                  {subHeading}
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed font-medium border-l-4 border-primary-500 pl-6 italic">
                  {post.excerpt}
                </p>
              </div>

              {/* Featured Image inside content */}
              <div className="mb-12 rounded-2xl overflow-hidden shadow-lg group">
                <img
                  src={getImageUrl(post.image_url)}
                  alt={post.title}
                  className="w-full h-auto object-cover transform transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              {/* Dynamic Long Description */}
              <div 
                className="prose prose-lg max-w-none text-gray-700 mb-12 blog-content-rich"
                dangerouslySetInnerHTML={{ __html: longDescription }}
              />

              <style>{`
                .blog-content-rich p {
                  margin-bottom: 1.5rem;
                  line-height: 1.8;
                  font-size: 1.125rem;
                }
                .blog-content-rich ul {
                  list-style-type: disc;
                  margin-left: 1.5rem;
                  margin-bottom: 1.5rem;
                }
                .blog-content-rich ol {
                  list-style-type: decimal;
                  margin-left: 1.5rem;
                  margin-bottom: 1.5rem;
                }
                .blog-content-rich li {
                  margin-bottom: 0.5rem;
                }
                .blog-content-rich h2, .blog-content-rich h3, .blog-content-rich h4 {
                  font-weight: bold;
                  margin-top: 2rem;
                  margin-bottom: 1rem;
                  color: #111827;
                }
                .blog-content-rich h2 { font-size: 1.875rem; }
                .blog-content-rich h3 { font-size: 1.5rem; }
                .blog-content-rich h4 { font-size: 1.25rem; }
                .blog-content-rich blockquote {
                  border-left: 4px solid #2563eb;
                  padding-left: 1.5rem;
                  font-style: italic;
                  margin-bottom: 1.5rem;
                  color: #4b5563;
                }
                .blog-content-rich a {
                  color: #2563eb;
                  text-decoration: underline;
                }
              `}</style>

              {/* Highlights Section */}
              {highlights.length > 0 && (
                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 mb-10">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                    Key Takeaways
                  </h3>
                  <ul className="space-y-4">
                    {highlights.map((point, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-start gap-4"
                      >
                        <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                        <span className="text-lg text-gray-700 font-medium">{point}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Conclusion / Call to Action */}
              <div className="bg-primary-50 rounded-2xl p-8 text-center border border-primary-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {ctaSettings?.title || "Want to Learn More About Home Loans?"}
                </h3>

                <p className="text-gray-600 mb-6 leading-relaxed max-w-2xl mx-auto">
                  {ctaSettings?.description || "We regularly publish expert insights, guides, and financial tips to help you make smarter decisions about buying, building, or upgrading your home. Stay informed with clear, practical, and real-world advice from AHAM’s housing experts."}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  {(ctaSettings?.content_data?.cards || [
                    { title: "Expert Insights", description: "Understand the latest market trends, interest rate updates, and smart financing tips.", is_active: true },
                    { title: "Step-By-Step Guides", description: "Practical walkthroughs to simplify your home loan journey — from eligibility to approval.", is_active: true },
                    { title: "Real Customer Stories", description: "Learn how families achieved their dream homes with AHAM, even without traditional documents.", is_active: true }
                  ]).filter(card => card.is_active !== false).map((card, idx) => (
                    <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border border-primary-100 hover:shadow-md transition-all">
                      <h4 className="font-semibold text-gray-900 mb-2 text-lg">
                        {card.title}
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>


            </motion.div>
          </div>

          {/* Right Sidebar Column */}
          <div className="lg:col-span-4 space-y-8">

            {/* Trending Widget */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Trending Now</h3>
                <span className="text-xs font-bold text-primary-600 bg-primary-50 px-2 py-1 rounded-md uppercase tracking-wide">Hot</span>
              </div>

              <div className="space-y-6">
                {trendingPosts.map((item) => (
                  <Link
                    key={item.id}
                    to={`/home-loan-blog-aham-housing-finance/${item.id}`}
                    className="group flex gap-4 items-start"
                  >
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 relative">
                      <img
                        src={getImageUrl(item.image_url)}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        alt={item.title}
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold text-primary-600 mb-1 block">
                        {item.category}
                      </span>
                      <h4 className="text-base font-bold text-gray-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {item.title}
                      </h4>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {item.date}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              <Link
                to="/home-loan-blog-aham-housing-finance"
                className="block mt-8 text-center w-full py-3 rounded-lg border-2 border-gray-100 text-gray-600 font-semibold hover:border-primary-600 hover:text-primary-600 transition-all"
              >
                View All Articles
              </Link>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogInner;
